// 앱(Capacitor Android) 전용 지문/생체 잠금. 2026-10-16 - 416일 토큰 → 짧은 액세스 토큰 +
// 기기별 리프레시 세션 전환(Part 1)에 이어, 리프레시 세션은 그대로 오래 유지하고 "앱을 N분 이상
// 백그라운드에 뒀다가 다시 열면 지문/얼굴(또는 기기 PIN/패턴)을 요구"하는 화면 잠금을 추가한다(Part 2).
//
// 플러그인: @aparajita/capacitor-biometric-auth (MIT, Capacitor 7 대상 9.x 라인) - 패스워드나
// 다른 비밀을 저장하지 않는다. 이미 쿠키(HttpOnly refresh_token)로 유지되는 리프레시 세션을
// "보여줄지 말지"만 이 잠금화면이 게이트한다 - authenticate()가 성공하면 그냥 화면을 보여주고,
// 실패/취소하면 계속 가려둔다. 그래서 Android Keystore에 별도로 넣어야 할 비밀이 없다.
//
// 웹 브라우저에서는 전부 no-op(Capacitor.isNativePlatform()===false) - PushSettings.js 등과
// 같은 관례.

import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

const ENABLED_KEY = 'kmes-biometric-enabled';
const TIMEOUT_MIN_KEY = 'kmes-lock-timeout-min';
const PROMPTED_KEY = 'kmes-biometric-prompted';
const BACKGROUNDED_AT_KEY = 'kmes-backgrounded-at';

export const DEFAULT_LOCK_TIMEOUT_MIN = 5;

export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function safeStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isBiometricEnabled() {
  return safeStorage()?.getItem(ENABLED_KEY) === '1';
}

export function setBiometricEnabled(enabled) {
  const storage = safeStorage();
  if (!storage) return;
  if (enabled) {
    storage.setItem(ENABLED_KEY, '1');
  } else {
    storage.removeItem(ENABLED_KEY);
  }
}

export function hasPromptedBefore() {
  return safeStorage()?.getItem(PROMPTED_KEY) === '1';
}

export function markPrompted() {
  safeStorage()?.setItem(PROMPTED_KEY, '1');
}

export function getLockTimeoutMinutes() {
  const raw = safeStorage()?.getItem(TIMEOUT_MIN_KEY);
  const parsed = raw != null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LOCK_TIMEOUT_MIN;
}

export function setLockTimeoutMinutes(minutes) {
  const storage = safeStorage();
  if (!storage) return;
  const n = parseInt(minutes, 10);
  if (Number.isFinite(n) && n > 0) {
    storage.setItem(TIMEOUT_MIN_KEY, String(n));
  }
}

// ---- 백그라운드 경과시간 추적 (순수 로직은 테스트 대상으로 분리) ----

export function recordBackgroundedAt(now = Date.now()) {
  safeStorage()?.setItem(BACKGROUNDED_AT_KEY, String(now));
}

export function clearBackgroundedAt() {
  safeStorage()?.removeItem(BACKGROUNDED_AT_KEY);
}

function getBackgroundedAt() {
  const raw = safeStorage()?.getItem(BACKGROUNDED_AT_KEY);
  const n = raw != null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * 잠금을 걸어야 하는지 판단하는 순수 함수(유닛테스트 대상).
 * backgroundedAt이 null이면(기록이 없음 = 콜드스타트 등) 안전하게 "잠근다"로 판단한다.
 */
export function shouldLock({ enabled, backgroundedAt, now = Date.now(), timeoutMinutes = DEFAULT_LOCK_TIMEOUT_MIN }) {
  if (!enabled) return false;
  if (backgroundedAt == null) return true;
  return now - backgroundedAt >= timeoutMinutes * 60 * 1000;
}

/** resume 시점에 잠금 여부를 판단 — App.js의 리스너가 호출. */
export function shouldLockNow() {
  return shouldLock({
    enabled: isBiometricEnabled(),
    backgroundedAt: getBackgroundedAt(),
    timeoutMinutes: getLockTimeoutMinutes(),
  });
}

// ---- 플러그인 래핑 ----

/**
 * 생체 인증 가능 여부. 웹에서는 플러그인이 "시뮬레이션"하므로 호출은 되지만, 우리는 isNative()가
 * false면 애초에 잠금화면을 띄우지 않으므로 호출 자체가 안 일어난다.
 */
export async function checkBiometryAvailable() {
  try {
    const result = await BiometricAuth.checkBiometry();
    return result;
  } catch (e) {
    return { isAvailable: false, reason: e?.message, code: e?.code };
  }
}

/**
 * 지문/얼굴 인증 — 실패/하드웨어 미지원이면 기기 PIN/패턴으로 바로 넘어간다(allowDeviceCredential).
 * 성공하면 true, 사용자가 취소(Cancel)하면 false, 그 외 에러도 false(잠금 화면에 머문다).
 */
export async function authenticateBiometric(reason = 'KMES 잠금을 해제하려면 인증하세요') {
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: '취소',
      allowDeviceCredential: true,
      androidTitle: 'KMES 잠금 해제',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch (e) {
    // BiometryError(userCancel 포함) - 실패/취소 모두 "계속 잠금" 쪽으로 처리.
    return false;
  }
}

// JWT access_token의 exp(초, UNIX epoch) claim만 가볍게 파싱 - 서명 검증은 서버가 하므로 여기선
// "리프레시가 필요한가"만 보는 용도(틀려도 다음 API 호출에서 401→리프레시로 안전하게 복구됨).
export function decodeJwtExpSeconds(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

export function isAccessTokenExpired(token, now = Date.now()) {
  const expSec = decodeJwtExpSeconds(token);
  if (expSec == null) return false; // 못 읽으면 보수적으로 "만료 아님"(다음 401이 알아서 처리)
  return now >= expSec * 1000;
}
